import { Container, Graphics, Rectangle, Sprite, Texture } from "pixi.js";
import { type Ball } from "@golazo/shared";
import { lerp } from "../core/math.js";
import { drawTango } from "./tangoPattern.js";
import { createSoftShadow, getTextures } from "./textures.js";

export interface BallLayer {
  container: Container;
  sync(alpha: number, renderDt: number): void;
}

/**
 * La tira de `textures/ball.png`. Sale de `tools/blender/make_ball.py` y estos
 * numeros tienen que casar con los de alli (FRAMES, COLS, CELL, SPAN).
 */
const BALL_FRAMES = 16;
const BALL_COLS = 4;
const BALL_CELL = 64;
/** El fotograma abarca [-SPAN, SPAN] radios: hay aire alrededor de la pelota. */
const BALL_SPAN = 1.04;
/**
 * Un balon clasico girado 72 grados sobre el eje de un pentagono queda igual,
 * asi que la tira solo cubre 72 grados de rodadura y luego se repite.
 */
const ROLL_PERIOD = (Math.PI * 2) / 5;
/**
 * Tope de rodadura por fotograma PINTADO, en fraccion del periodo. Un chut
 * gira mas de un periodo entero entre dos refrescos, y entonces la tira
 * aliasea: parece que rueda hacia atras o que se para (el efecto de las
 * ruedas de carro en las peliculas). Por encima del tope rueda "a tope" y
 * punto, que ademas es cuando el estirado de velocidad ya lo esta
 * emborronando.
 */
const MAX_ROLL_STEP = 0.4 * ROLL_PERIOD;
/**
 * Lo rapido que la tira se orienta hacia la nueva direccion (1/s). Girarla de
 * golpe en cada rebote haria saltar el dibujo; con esto gira en ~0,1 s, que
 * se lee como el balon cambiando de eje.
 */
const HEADING_RATE = 28;

/** Angulo en (-PI, PI]. */
function wrapAngle(a: number): number {
  return a - Math.PI * 2 * Math.floor((a + Math.PI) / (Math.PI * 2));
}

export function createBallLayer(ball: Ball): BallLayer {
  const layer = new Container();
  layer.label = "ball";

  const r = ball.radius;

  // Contenedor y no la sombra suelta: el resto del codigo mueve, escala y
  // atenua `shadow` segun la altura del balon, y con el desplazamiento dentro
  // esa logica sigue valiendo igual con elipse o con textura.
  const shadow = new Container();
  const softShadow = createSoftShadow(r * 0.85, r * 0.3);
  if (softShadow) {
    softShadow.y = r * 0.85;
    shadow.addChild(softShadow);
  } else {
    shadow.addChild(
      new Graphics().ellipse(0, r * 0.85, r * 0.85, r * 0.3).fill({ color: 0x000000, alpha: 0.25 }),
    );
  }
  layer.addChild(shadow);

  // `root` lleva posicion y altura. `squash` estira a lo largo de la marcha:
  // se gira hacia donde va el balon y se escala en ESE eje. `upright` deshace
  // el giro, asi que todo lo de dentro se estira sin girar. Sin esa vuelta,
  // la luz de `sphere` giraria con la direccion del balon y el brillo daria
  // vueltas en cada pase.
  const root = new Container();
  const squash = new Container();
  const upright = new Container();
  root.addChild(squash);
  squash.addChild(upright);
  layer.addChild(root);

  const tex = getTextures();

  // Con la tira de Blender el balon RUEDA: el dibujo avanza en la direccion
  // de la marcha. Sin texturas (o si no cargan) queda el Tango vectorial de
  // siempre, que gira como un disco.
  let frames: Texture[] | null = null;
  let rolling: Sprite | null = null;
  let tango: Container | null = null;

  if (tex) {
    frames = [];
    for (let i = 0; i < BALL_FRAMES; i++) {
      const col = i % BALL_COLS;
      const row = Math.floor(i / BALL_COLS);
      frames.push(
        new Texture({
          source: tex.ball.source,
          frame: new Rectangle(col * BALL_CELL, row * BALL_CELL, BALL_CELL, BALL_CELL),
        }),
      );
    }
    rolling = new Sprite(frames[0]);
    rolling.anchor.set(0.5);
    rolling.width = r * 2 * BALL_SPAN;
    rolling.height = r * 2 * BALL_SPAN;
    upright.addChild(rolling);
  } else {
    tango = new Container();
    const body = new Graphics();
    drawTango(body, r);
    tango.addChild(body);
    const clip = new Graphics().circle(0, 0, r).fill(0xffffff);
    tango.addChild(clip);
    tango.mask = clip;
    upright.addChild(tango);
  }

  // La esfera iluminada va FUERA del dibujo: el balon gira bajo un foco
  // quieto. Si el sombreado girase con el dibujo, el brillo daria vueltas con
  // la pelota y se leeria como una calcomania, no como volumen.
  if (tex) {
    const shade = new Sprite(tex.sphere);
    shade.anchor.set(0.5);
    shade.width = r * 2;
    shade.height = r * 2;
    shade.blendMode = "multiply";
    upright.addChild(shade);
  }

  let spinAngle = 0;
  /** Hacia donde apunta el eje de rodadura de la tira (en pantalla). */
  let heading = 0;
  /** Lo rodado, en radianes; solo importa modulo ROLL_PERIOD. */
  let roll = 0;

  return {
    container: layer,
    sync(alpha: number, renderDt: number): void {
      const x = lerp(ball.prevPos.x, ball.pos.x, alpha);
      const y = lerp(ball.prevPos.y, ball.pos.y, alpha);
      const loft = lerp(ball.prevLoft, ball.loft, alpha);

      shadow.x = x;
      shadow.y = y;
      const shadowScale = Math.max(0.55, 1 - loft * 0.014);
      shadow.scale.set(shadowScale, shadowScale * 0.85);
      // La textura ya trae su propio alfa (pico 0,64): estos numeros van
      // divididos por ese pico para que la sombra pese lo mismo que la elipse
      // plana a la que sustituye.
      const shadowAlpha = softShadow ? 0.44 : 0.28;
      const shadowFloor = softShadow ? 0.13 : 0.08;
      shadow.alpha = Math.max(shadowFloor, shadowAlpha - loft * (softShadow ? 0.008 : 0.005));

      root.x = x;
      root.y = y - loft;
      const airScale = 1 + loft * 0.004;
      root.scale.set(airScale, airScale);

      const { x: vx, y: vy } = ball.vel;
      const speed = Math.hypot(vx, vy);

      if (rolling && frames) {
        if (speed > 1) {
          // La tira rueda igual hacia delante que hacia atras (avanzando o
          // retrocediendo fotogramas), asi que el eje nunca tiene que girar
          // mas de 90 grados: se orienta hacia la marcha o hacia su contraria,
          // la que este mas cerca. Un rebote de frente no gira nada; solo
          // invierte el sentido.
          const travel = Math.atan2(vy, vx);
          let turn = wrapAngle(travel - heading);
          if (turn > Math.PI / 2) turn -= Math.PI;
          else if (turn < -Math.PI / 2) turn += Math.PI;
          heading += turn * (1 - Math.exp(-HEADING_RATE * renderDt));
        }
        // Rueda lo que avanza el balon A LO LARGO del eje de la tira. Mientras
        // el eje se esta orientando, la parte de lado no rueda: dura ~0,1 s.
        const along = vx * Math.cos(heading) + vy * Math.sin(heading);
        const step = (along / Math.max(r, 4)) * renderDt;
        roll += Math.max(-MAX_ROLL_STEP, Math.min(MAX_ROLL_STEP, step));
        roll -= ROLL_PERIOD * Math.floor(roll / ROLL_PERIOD);
        const frame = Math.floor((roll / ROLL_PERIOD) * BALL_FRAMES) % BALL_FRAMES;
        rolling.texture = frames[frame]!;
        rolling.rotation = heading;
      } else if (tango) {
        spinAngle += ball.spin * renderDt;
        if (speed > 12) spinAngle += (speed / Math.max(r, 4)) * renderDt;
        tango.rotation = spinAngle;
      }

      const stretch = Math.min(1.14, 1 + speed / 4200);
      if (speed > 40) {
        const dir = Math.atan2(vy, vx);
        squash.rotation = dir;
        upright.rotation = -dir;
        squash.scale.set(stretch, 1 / stretch);
      } else {
        squash.rotation = 0;
        upright.rotation = 0;
        squash.scale.set(1, 1);
      }
    },
  };
}
